import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Rocket, X, Download, TrendingUp, TrendingDown, Minus, AlertTriangle, Info, CalendarDays } from "lucide-react";

const money = (v, cur = "EUR") => {
  try { return new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur }).format(Number(v) || 0); } catch { return `${Number(v) || 0} ${cur}`; }
};
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const PLATFORM_LABEL = { web: "Web", android: "Android", ios: "iOS" };
const LEVEL_STYLE = {
  danger: { background: "#FEE2E2", color: "#991B1B" },
  warn: { background: "#FEF3C7", color: "#92400E" },
  info: { background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" },
};

const Kpi = ({ label, value, sub, testId }) => (
  <div className="rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
    <div className="text-lg font-bold tabular-nums leading-tight mt-0.5">{value}</div>
    {sub && <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{sub}</div>}
  </div>
);

// Carte « 7 premiers jours » : visible du premier paiement RÉEL jusqu'à J+7 (masquable par le super admin).
export const LaunchReportCard = ({ isSuper = false }) => {
  const [rep, setRep] = useState(null);
  useEffect(() => { api.get("/admin/finance/launch-report").then(({ data }) => setRep(data)).catch(() => setRep(null)); }, []);
  if (!rep?.started || !rep.active) return null;
  const s = rep.stats, d = s.donations, w = s.withdrawals, cur = s.currency;
  const dismiss = async () => {
    try { await api.post("/admin/finance/launch-report/dismiss"); setRep((r) => ({ ...r, active: false })); toast.success("Carte de lancement masquée"); }
    catch { toast.error("Impossible de masquer"); }
  };
  return (
    <div className="yalola-card p-4 space-y-3" style={{ borderColor: "var(--yalola-primary)", borderWidth: 1 }} data-testid="finance-launch-card" data-day={rep.day}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Rocket size={18} style={{ color: "var(--yalola-primary)" }} className="mt-0.5 shrink-0" />
          <div>
            <h3 className="font-semibold">Bilan de lancement — jour {rep.day} / 7 de paiements réels</h3>
            <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
              Premier paiement réel le <b>{fmtDateTime(rep.first_real_at)}</b> · cette carte disparaît d'elle-même le {fmtDateTime(rep.ends_at)}. Données réelles uniquement (aucun test).
            </p>
          </div>
        </div>
        {isSuper && (
          <button type="button" onClick={dismiss} className="h-8 w-8 rounded-full grid place-items-center hover:bg-black/5" aria-label="Masquer" data-testid="finance-launch-dismiss"><X size={15} /></button>
        )}
      </div>
      <div className="flex gap-1" aria-hidden="true" data-testid="finance-launch-progress">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => <div key={i} className="h-1.5 flex-1 rounded-full" style={{ background: i <= rep.day ? "var(--yalola-primary)" : "var(--yalola-border)" }} />)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi label="Dons payés" value={`${d.count} · ${money(d.gross, cur)}`} sub={`panier moyen ${money(d.avg, cur)} · ${d.donors} donateur(s) → ${d.receivers} bénéficiaire(s)`} testId="finance-launch-kpi-donations" />
        <Kpi label="Frais facturés vs Stripe réels" value={`${money(d.fees_charged, cur)} / ${money(d.fees_actual, cur)}`} sub={`écart ${d.fee_variance >= 0 ? "+" : ""}${money(d.fee_variance, cur)}`} testId="finance-launch-kpi-fees" />
        <Kpi label="Reversé aux membres" value={money(d.net_to_members, cur)} sub={`revenu net Yalola ${money(s.yalola_net, cur)} = commissions + Vérifié net + crédits pub nets (brut ${money((s.verifications?.gross || 0) + (s.ad_credits?.gross || 0), cur)} − frais stores ${money(s.store_fees, cur)} − TVA stores ${money(s.store_tax, cur)})`} testId="finance-launch-kpi-members" />
        <Kpi label="Retraits" value={`${w.paid_count} payé(s) · ${money(w.paid_amount, cur)}`} sub={`${w.requested_count} demandé(s) · ${w.open_count} en attente (${money(w.open_amount, cur)})`} testId="finance-launch-kpi-withdrawals" />
      </div>
      <div className="flex flex-wrap gap-2 text-xs" data-testid="finance-launch-platforms">
        {Object.entries(d.by_platform || {}).map(([p, r]) => (
          <span key={p} className="px-2.5 py-1 rounded-full" style={{ background: "var(--yalola-surface-alt)" }}>{PLATFORM_LABEL[p] || p} : {r.count} · {money(r.gross, cur)}</span>
        ))}
        <span className="px-2.5 py-1 rounded-full" style={{ background: "var(--yalola-surface-alt)" }}>Vérifié : {s.verifications.count} · {money(s.verifications.gross, cur)}</span>
        <span className="px-2.5 py-1 rounded-full" style={{ background: "var(--yalola-surface-alt)" }}>Crédits pub : {s.ad_credits.count} · {money(s.ad_credits.gross, cur)}</span>
        {d.failed_attempts > 0 && <span className="px-2.5 py-1 rounded-full" style={LEVEL_STYLE.warn}>{d.failed_attempts} paiement(s) non abouti(s)</span>}
      </div>
      {rep.alerts?.length > 0 && (
        <ul className="space-y-1.5" data-testid="finance-launch-alerts">
          {rep.alerts.map((a) => (
            <li key={a.key} className="flex items-start gap-2 rounded-lg px-3 py-2 text-xs" style={LEVEL_STYLE[a.level] || LEVEL_STYLE.info} data-testid={`finance-launch-alert-${a.key}`}>
              {a.level === "info" ? <Info size={13} className="shrink-0 mt-0.5" /> : <AlertTriangle size={13} className="shrink-0 mt-0.5" />}<span>{a.text}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Un résumé est envoyé aux super admins (notification + e-mail) aux jours 1, 3 et 7.</p>
    </div>
  );
};

const Delta = ({ v, testId }) => {
  if (v === null || v === undefined) return <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid={testId}>—</span>;
  const up = v > 0, flat = v === 0;
  const color = flat ? "var(--yalola-text-muted)" : up ? "#166534" : "#B91C1C";
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums" style={{ color }} data-testid={testId}><Icon size={11} />{up ? "+" : ""}{v} %</span>;
};

// Bilan hebdomadaire permanent : chaque semaine ISO comparée à la précédente (Δ en %).
export const WeeklyReportCard = () => {
  const [rep, setRep] = useState(null);
  const [weeks, setWeeks] = useState(8);
  const [exporting, setExporting] = useState(false);
  useEffect(() => { setRep(null); api.get(`/admin/finance/weekly-report?weeks=${weeks}`).then(({ data }) => setRep(data)).catch(() => setRep({ weeks: [] })); }, [weeks]);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data: blob } = await api.get(`/admin/finance/weekly-report.csv?weeks=${Math.max(weeks, 12)}`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "yalola-bilan-hebdomadaire.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
    finally { setExporting(false); }
  };
  const cur = rep?.currency || "EUR";
  return (
    <div className="yalola-card p-4 space-y-3" data-testid="finance-weekly-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarDays size={18} style={{ color: "var(--yalola-primary)" }} />
          <div>
            <h3 className="font-semibold">Bilan hebdomadaire</h3>
            <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Paiements réels, semaine ISO (lundi → dimanche), chaque semaine comparée à la précédente. Digest envoyé aux super admins le lundi matin.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} className="h-8 rounded-full border px-3 text-xs bg-transparent" style={{ borderColor: "var(--yalola-border)" }} data-testid="finance-weekly-range">
            {[4, 8, 12, 26].map((n) => <option key={n} value={n}>{n} semaines</option>)}
          </select>
          <Button size="sm" variant="outline" className="rounded-full" onClick={exportCsv} disabled={exporting} data-testid="finance-weekly-export"><Download size={13} className="mr-1" /> {exporting ? "…" : "CSV"}</Button>
        </div>
      </div>
      {!rep ? (
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Chargement…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="finance-weekly-table">
            <thead>
              <tr className="text-left" style={{ color: "var(--yalola-text-muted)" }}>
                <th className="py-1.5 pr-3 font-medium">Semaine</th>
                <th className="py-1.5 pr-3 font-medium">Dons</th>
                <th className="py-1.5 pr-3 font-medium">Reversé</th>
                <th className="py-1.5 pr-3 font-medium">Écart frais</th>
                <th className="py-1.5 pr-3 font-medium">Vérifié (brut)</th>
                <th className="py-1.5 pr-3 font-medium">Crédits pub (brut)</th>
                <th className="py-1.5 pr-3 font-medium" title="Commissions sur dons + Vérifié net + crédits pub nets — net = brut − frais store/Stripe − TVA collectée par le store">Net Yalola</th>
                <th className="py-1.5 pr-3 font-medium">Retraits payés</th>
                <th className="py-1.5 font-medium" title="Tentatives de paiement non abouties : annulées, expirées ou refusées (jamais comptées)">Non abouties</th>
              </tr>
            </thead>
            <tbody>
              {rep.weeks.map((wk) => {
                const s = wk.stats, d = s.donations, dl = wk.delta || {};
                return (
                  <tr key={wk.key} className="border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid={`finance-weekly-row-${wk.key}`} data-partial={wk.partial ? "1" : "0"}>
                    <td className="py-2 pr-3 whitespace-nowrap font-medium">{wk.label}{wk.partial && <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px]" title="Semaine ISO non terminée : les chiffres évoluent jusqu'à dimanche soir" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid={`finance-weekly-partial-${wk.key}`}>semaine en cours</span>}</td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{d.count} · {money(d.gross, cur)} <Delta v={dl.donations_gross} testId={`finance-weekly-delta-${wk.key}`} /></td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{money(d.net_to_members, cur)} <Delta v={dl.net_to_members} /></td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums" style={{ color: d.fee_variance < -0.5 ? "#B91C1C" : undefined }}>{d.fee_variance >= 0 ? "+" : ""}{money(d.fee_variance, cur)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{s.verifications.count} · {money(s.verifications.gross, cur)} <Delta v={dl.verifications} /></td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{s.ad_credits.count} · {money(s.ad_credits.gross, cur)} <Delta v={dl.ad_credits} /></td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums font-semibold" title={`brut ${money((s.verifications?.gross || 0) + (s.ad_credits?.gross || 0), cur)} − frais stores ${money(s.store_fees, cur)} − TVA ${money(s.store_tax, cur)} + commissions ${money(d.commission, cur)}`} data-testid={`finance-weekly-net-${wk.key}`}>{money(s.yalola_net, cur)} <Delta v={dl.yalola_net} /></td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{s.withdrawals.paid_count} · {money(s.withdrawals.paid_amount, cur)} <Delta v={dl.withdrawals_paid} /></td>
                    <td className="py-2 whitespace-nowrap tabular-nums">{d.failed_attempts}{d.refunds ? ` · ${d.refunds} remb.` : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rep.first_real_at && <p className="text-xs mt-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="finance-weekly-empty">Aucun paiement réel pour l'instant — le bilan se remplira dès le premier encaissement en production.</p>}
          <p className="text-[11px] mt-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="finance-weekly-legend">Net Yalola = commissions sur dons + Vérifié net + crédits pub nets, où net = brut − frais du store ou de Stripe − TVA collectée par le store (App Store / Google Play). Même formule que la carte « Revenu net » et le PDF mensuel.</p>
        </div>
      )}
    </div>
  );
};
